import { useEffect, useState } from "react";

import { AdapterProtocol } from "../../../flatbuffers/generated/archive/adapter-protocol";
import { fetchFlatBufferBinary } from "../../../utils/flatbufferApi";
import { decodeOpcUaMapping } from "../../../flatbuffers/builders/opcUaCdoBuilder";
import type { AdapterCdoOpcUa } from "../../../interfaces/AdapterCdoOpcUa.interface";
import GETJwtToken from "../../../redux/authentication/authentication";

/**
 * Fetches and decodes the OPC UA CDO mapping for the State panel.
 *
 * Like Modbus, OPC UA's CDO is union-heavy (Identity, NodeIdentifier,
 * Data/Variant, MonitorMode, FilterOperand) so it cannot go through the
 * JSON `cdoTemplateJson` path — we pull the FlatBuffer binary from
 * `/adapters/{id}/cdo` and decode it via `decodeOpcUaMapping`, the same
 * path AdapterEditor and StepChannelModifiers use. Returns `null` for
 * non-OPCUA adapters, before the bytes resolve, or on any fetch/decode
 * failure (the State tab simply shows no OPC UA dictionary in that case
 * rather than crashing). Mirrors `useModbusCdoTemplate`.
 */
export default function useOpcUaCdoMapping(
  adapterId: string,
  protocolType: AdapterProtocol | undefined,
): AdapterCdoOpcUa | null {
  const [mapping, setMapping] = useState<AdapterCdoOpcUa | null>(null);

  useEffect(() => {
    let cancelled = false;

    if (!adapterId || protocolType !== AdapterProtocol.OPCUA) {
      setMapping(null);
      return undefined;
    }

    (async () => {
      try {
        const token = await GETJwtToken();
        const apiBase = process.env.REACT_APP_LIFT_NET_CLOUD_API_ONLINE || "";
        const bytes = await fetchFlatBufferBinary(`${apiBase}/adapters/${adapterId}/cdo`, token);
        if (cancelled) return;
        setMapping(bytes && bytes.length > 0 ? decodeOpcUaMapping(bytes) : null);
      } catch {
        if (!cancelled) setMapping(null);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [adapterId, protocolType]);

  return mapping;
}
